import React from "react";
import WComponent from "../../../../../common/WComponent";
import { formatReportAmount, formatCurrency, formatDate } from "../../shared/helpers/formatters";
import { NOTE_TEMPLATES, ASSET_CATS, CURRENT_LIAB_CATS, LT_LIAB_CATS, EQUITY_CATS } from "../../shared/helpers/constants";
import BookkeepingService from "../../../../../../services/BookkeepingService";
import "../../afs-walkthrough.css";

const fmtAmt = formatReportAmount;
const fmtCurrency = formatCurrency;
const fmtDate = formatDate;

class FullPreviewView extends WComponent {
	render() {
	let { compilationData, entityName, clientProfile, yearEnd, balanceSheet, incomeStatement, ppeNote, retainedEarnings, currentYear, priorYear, showComparatives, noteTexts, noteIds: noteIdsProp, customNotes } = this.props;

	const customNoteTitleById = BookkeepingService.buildCustomNoteTitleMap({ customNotes });
	const isCustomNote = id => Object.prototype.hasOwnProperty.call(customNoteTitleById, id);
	const hasNoteBody = id => !!(noteTexts && noteTexts[id]);
	const yearEndLabel = yearEnd ? fmtDate(yearEnd) : "[Year End]";
	const reportDate   = compilationData.noticeDate ? fmtDate(compilationData.noticeDate) : new Date().toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
	const entity       = (clientProfile && clientProfile.entityName) || entityName || "[Entity Name]";
	const firm         = compilationData.firm || "";
	const firmCity     = (clientProfile && clientProfile.city && clientProfile.province)
		? `${clientProfile.city}, ${clientProfile.province}` : "";

	const cols = showComparatives ? 2 : 1;
	const selectedNotes = noteIdsProp || (compilationData.notes || []).filter(id => NOTE_TEMPLATES[id]);

	// Header row used across all financial statement pages
	const PageHeader = ({ title, subtitle }) => (
		<>
			<p className="afs-report__entity-name">{entity}</p>
			<p className={`afs-report__page-title ${subtitle ? "afs-report__page-title--flush" : "afs-report__page-title--spaced"}`}>{title}</p>
			{subtitle && <p className="afs-report__page-subtitle">{subtitle}</p>}
		</>
	);

	const YearHeaders = () => (
		<tr>
			<th />
			<th className="afs-report__th-right">{currentYear}</th>
			{showComparatives && <th className="afs-report__th-right">{priorYear}</th>}
		</tr>
	);

	// ─── Page 1: Title Page ──────────────────────────────────────────────────
	const TitlePage = () => (
		<div className="afs-report__page afs-report__page--cover">
			<p className="afs-report__entity-name afs-report__entity-name--cover">{entity}</p>
			<p className="afs-report__cover-line afs-report__cover-line--gap">Financial Information</p>
			<p className="afs-report__cover-line">Year Ended {yearEndLabel}</p>
		</div>
	);

	// ─── Page 2: Index ───────────────────────────────────────────────────────
	const IndexPage = () => {
		const items = [
			{ label: "Compilation Engagement Report", page: 1 },
			{ label: "Balance Sheet", page: 2 },
			{ label: "Statement of Retained Earnings", page: 3 },
			{ label: "Statement of Income", page: 4 },
		];
		if (selectedNotes.length > 0) items.push({ label: "Notes to Financial Information", page: 5 });

		return (
			<div className="afs-report__page">
				<PageHeader title="Index to Financial Information" subtitle={`Year Ended ${yearEndLabel}`} />
				<table className="afs-report__table afs-report__table--mt-xl">
					<thead>
						<tr><th className="afs-report__index-th" /><th className="afs-report__index-th--right">Page</th></tr>
					</thead>
					<tbody>
						{items.map((item, i) => (
							<tr key={i}>
								<td className={`afs-report__index-td${item.label === "Compilation Engagement Report" ? " afs-report__index-td--strong" : ""}`}>{item.label}</td>
								<td className="afs-report__index-td--page">{item.page}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	};

	// ─── Page 3: Compilation Engagement Report ───────────────────────────────
	const CompilationPage = () => (
		<div className="afs-report__page">
			<h2 className="afs-report__h2 afs-report__h2--report">Compilation Engagement Report</h2>
			<p className="afs-report__text afs-report__text--addressee">To the Management of {entity}</p>
			<p className="afs-report__text">{compilationData.compilationNotice}</p>
			<div className="afs-report__signature">
				{firmCity && <p className="afs-report__text-sm afs-report__text-sm--tight">{firmCity}</p>}
				{firm && <p className="afs-report__text-sm afs-report__text-sm--firm">{firm}</p>}
				<p className="afs-report__text-sm afs-report__text-sm--tight">{reportDate}</p>
				<p className="afs-report__text-sm afs-report__text-sm--designation">Chartered Professional Accountants</p>
			</div>
			<p className="afs-report__page-num">1</p>
		</div>
	);

	// ─── Page 4: Balance Sheet ───────────────────────────────────────────────
	const BalanceSheetPage = () => {
		if (!balanceSheet) return null;

		const renderSection = (section) => (
			<React.Fragment key={section.name}>
				{section.accounts.map((a, i) => (
					<tr key={i}>
						<td className="afs-report__td-desc-indent">{a.description}</td>
						<td className="afs-report__td-amt">{fmtAmt(a.current)}</td>
						{showComparatives && <td className="afs-report__td-amt">{fmtAmt(a.prior)}</td>}
					</tr>
				))}
			</React.Fragment>
		);

		// Group sections by BS category
		const assets = balanceSheet.sections.filter(s => ASSET_CATS.has(s.category));
		const currentLiab = balanceSheet.sections.filter(s => CURRENT_LIAB_CATS.has(s.category));
		const ltLiab = balanceSheet.sections.filter(s => LT_LIAB_CATS.has(s.category));
		const equity = balanceSheet.sections.filter(s => EQUITY_CATS.has(s.category));

		const SectionGroup = ({ label, sections, showSubtotal, subtotalLabel }) => (
			<>
				{label && <tr><td className="afs-report__subhead" colSpan={1 + cols}>{label}</td></tr>}
				{sections.map(s => (
					<React.Fragment key={s.name}>
						{sections.length > 1 && <tr><td className="afs-report__td-desc afs-report__td-desc--group-pt" colSpan={1 + cols}>{s.name}</td></tr>}
						{renderSection(s)}
						{sections.length > 1 && (
							<tr>
								<td className="afs-report__td-desc afs-report__td-desc--group">&nbsp;</td>
								<td className="afs-report__td-amt afs-report__td-amt--ruled">{fmtAmt(s.totalCurrent)}</td>
								{showComparatives && <td className="afs-report__td-amt afs-report__td-amt--ruled">{fmtAmt(s.totalPrior)}</td>}
							</tr>
						)}
					</React.Fragment>
				))}
				{showSubtotal && (
					<tr className="afs-report__total-row">
						<td className="afs-report__td-desc afs-report__td-desc--bold">{subtotalLabel}</td>
						<td className="afs-report__td-amt-bold">{fmtAmt(sections.reduce((s, sec) => s + (sec.totalCurrent || 0), 0))}</td>
						{showComparatives && <td className="afs-report__td-amt-bold">{fmtAmt(sections.reduce((s, sec) => s + (sec.totalPrior || 0), 0))}</td>}
					</tr>
				)}
			</>
		);

		return (
			<div className="afs-report__page">
				<PageHeader title="Balance Sheet" subtitle={yearEndLabel} />
				<table className="afs-report__table afs-report__table--mt-md">
					<thead><YearHeaders /></thead>
					<tbody>
						<tr><td className="afs-report__section-head" colSpan={1 + cols}>Assets</td></tr>
						<SectionGroup label="Current" sections={assets.filter(s => s.category === 'current-assets' || s.category === 'assets')} showSubtotal={false} />
						{assets.filter(s => !['current-assets', 'assets'].includes(s.category)).map(s => (
							<React.Fragment key={s.name}>
								{renderSection(s)}
								<tr>
									<td className="afs-report__td-desc afs-report__td-desc--group-upper">{s.name}</td>
									<td className="afs-report__td-amt">{fmtAmt(s.totalCurrent)}</td>
									{showComparatives && <td className="afs-report__td-amt">{fmtAmt(s.totalPrior)}</td>}
								</tr>
							</React.Fragment>
						))}
						<tr className="afs-report__grand-total-row">
							<td className="afs-report__td-desc afs-report__td-desc--bold">Total Assets</td>
							<td className="afs-report__td-amt-bold">{fmtCurrency(balanceSheet.totalAssets.current)}</td>
							{showComparatives && <td className="afs-report__td-amt-bold">{fmtCurrency(balanceSheet.totalAssets.prior)}</td>}
						</tr>

						<tr><td className="afs-report__spacer-lg" colSpan={1 + cols} /></tr>
						<tr><td className="afs-report__section-head" colSpan={1 + cols}>Liabilities and Shareholder's Equity</td></tr>
						<SectionGroup label="Current" sections={currentLiab} showSubtotal={currentLiab.length > 0} subtotalLabel="Total Current Liabilities" />
						{ltLiab.length > 0 && <SectionGroup label="Long-term" sections={ltLiab} showSubtotal={false} />}
						{equity.length > 0 && (
							<>
								<tr><td className="afs-report__subhead" colSpan={1 + cols}>Shareholder's Equity</td></tr>
								{equity.map(s => renderSection(s))}
								<tr className="afs-report__total-row">
									<td className="afs-report__td-desc afs-report__td-desc--bold">&nbsp;</td>
									<td className="afs-report__td-amt-bold">{fmtAmt(equity.reduce((s, sec) => s + (sec.totalCurrent || 0), 0))}</td>
									{showComparatives && <td className="afs-report__td-amt-bold">{fmtAmt(equity.reduce((s, sec) => s + (sec.totalPrior || 0), 0))}</td>}
								</tr>
							</>
						)}
						<tr className="afs-report__grand-total-row">
							<td className="afs-report__td-desc afs-report__td-desc--bold">Total Liabilities and Equity</td>
							<td className="afs-report__td-amt-bold">{fmtCurrency(balanceSheet.totalLiabilitiesAndEquity.current)}</td>
							{showComparatives && <td className="afs-report__td-amt-bold">{fmtCurrency(balanceSheet.totalLiabilitiesAndEquity.prior)}</td>}
						</tr>
					</tbody>
				</table>
				<p className="afs-report__text-sm afs-report__approval">APPROVED BY THE DIRECTOR</p>
				<p className="afs-report__text-sm afs-report__signature-line">_____________________________ Director</p>
				<p className="afs-report__page-num">2</p>
			</div>
		);
	};

	// ─── Page 5: Statement of Retained Earnings ──────────────────────────────
	const RetainedEarningsPage = () => {
		if (!retainedEarnings) return null;
		return (
			<div className="afs-report__page">
				<PageHeader title="Statement of Retained Earnings" subtitle={`Year Ended ${yearEndLabel}`} />
				<table className="afs-report__table afs-report__table--mt-lg">
					<thead><YearHeaders /></thead>
					<tbody>
						<tr>
							<td className="afs-report__td-desc afs-report__td-desc--head">Retained Earnings — Beginning of Year</td>
							<td className="afs-report__td-amt">{fmtCurrency(retainedEarnings.openingBalance.current)}</td>
							{showComparatives && <td className="afs-report__td-amt">{fmtCurrency(retainedEarnings.openingBalance.prior)}</td>}
						</tr>
						<tr>
							<td className="afs-report__td-desc afs-report__td-desc--head">Net Income</td>
							<td className="afs-report__td-amt">{fmtAmt(retainedEarnings.netIncome.current)}</td>
							{showComparatives && <td className="afs-report__td-amt">{fmtAmt(retainedEarnings.netIncome.prior)}</td>}
						</tr>
						<tr className="afs-report__grand-total-row">
							<td className="afs-report__td-desc afs-report__td-desc--head">Retained Earnings — End of Year</td>
							<td className="afs-report__td-amt-bold">{fmtCurrency(retainedEarnings.closingBalance.current)}</td>
							{showComparatives && <td className="afs-report__td-amt-bold">{fmtCurrency(retainedEarnings.closingBalance.prior)}</td>}
						</tr>
					</tbody>
				</table>
				<p className="afs-report__page-num">3</p>
			</div>
		);
	};

	// ─── Page 6: Statement of Income ─────────────────────────────────────────
	const IncomeStatementPage = () => {
		if (!incomeStatement) return null;

		const renderISSection = (section) => section.accounts.map((a, i) => (
			<tr key={i}>
				<td className="afs-report__td-desc-indent">{a.description}</td>
				<td className="afs-report__td-amt">{fmtAmt(a.current)}</td>
				{showComparatives && <td className="afs-report__td-amt">{fmtAmt(a.prior)}</td>}
			</tr>
		));

		const revenue  = incomeStatement.sections.filter(s => s.category === 'revenue');
		const cogs     = incomeStatement.sections.filter(s => s.category === 'cogs');
		const expenses = incomeStatement.sections.filter(s => s.category === 'expenses');
		const other    = incomeStatement.sections.filter(s => s.category === 'other-income');
		const tax      = incomeStatement.sections.filter(s => s.category === 'tax');

		const totalRevCur   = revenue.reduce((s, sec) => s + (sec.totalCurrent || 0), 0);
		const totalRevPrior = revenue.reduce((s, sec) => s + (sec.totalPrior || 0), 0);
		const totalCogsCur   = cogs.reduce((s, sec) => s + (sec.totalCurrent || 0), 0);
		const totalCogsPrior = cogs.reduce((s, sec) => s + (sec.totalPrior || 0), 0);
		const grossCur   = totalRevCur - totalCogsCur;
		const grossPrior = totalRevPrior - totalCogsPrior;
		const grossPct      = totalRevCur ? ((grossCur / totalRevCur) * 100).toFixed(2) : 0;
		const grossPctPrior = totalRevPrior ? ((grossPrior / totalRevPrior) * 100).toFixed(2) : 0;
		const totalExpCur   = expenses.reduce((s, sec) => s + (sec.totalCurrent || 0), 0);
		const totalExpPrior = expenses.reduce((s, sec) => s + (sec.totalPrior || 0), 0);
		const opIncomeCur   = grossCur - totalExpCur;
		const opIncomePrior = grossPrior - totalExpPrior;
		const totalOtherCur   = other.reduce((s, sec) => s + (sec.totalCurrent || 0), 0);
		const totalOtherPrior = other.reduce((s, sec) => s + (sec.totalPrior || 0), 0);
		const beforeTaxCur   = opIncomeCur + totalOtherCur;
		const beforeTaxPrior = opIncomePrior + totalOtherPrior;
		return (
			<div className="afs-report__page">
				<PageHeader title="Statement of Income" subtitle={`Year Ended ${yearEndLabel}`} />
				<table className="afs-report__table afs-report__table--mt-md">
					<thead><YearHeaders /></thead>
					<tbody>
						{/* Revenue */}
						{revenue.map(s => (
							<React.Fragment key={s.name}>
								<tr><td className="afs-report__section-head" colSpan={1 + cols}>{s.name}</td></tr>
								{s.accounts.length > 1 ? renderISSection(s) : null}
								<tr>
									<td />
									<td className="afs-report__td-amt-bold">{fmtAmt(s.totalCurrent)}</td>
									{showComparatives && <td className="afs-report__td-amt-bold">{fmtAmt(s.totalPrior)}</td>}
								</tr>
							</React.Fragment>
						))}

						{/* COGS */}
						{cogs.map(s => (
							<React.Fragment key={s.name}>
								<tr><td className="afs-report__section-head afs-report__section-head--normal" colSpan={1 + cols}>{s.name}</td></tr>
								{s.accounts.length > 1 ? renderISSection(s) : null}
								<tr>
									<td />
									<td className="afs-report__td-amt">{fmtAmt(s.totalCurrent)}</td>
									{showComparatives && <td className="afs-report__td-amt">{fmtAmt(s.totalPrior)}</td>}
								</tr>
							</React.Fragment>
						))}

						{/* Gross Profit */}
						<tr className="afs-report__total-row">
							<td className="afs-report__td-desc afs-report__td-desc--head">Gross Profit ({grossPct}%; {priorYear} — {grossPctPrior}%)</td>
							<td className="afs-report__td-amt-bold">{fmtAmt(grossCur)}</td>
							{showComparatives && <td className="afs-report__td-amt-bold">{fmtAmt(grossPrior)}</td>}
						</tr>

						{/* Expenses */}
						<tr><td className="afs-report__spacer-md" colSpan={1 + cols} /></tr>
						<tr><td className="afs-report__section-head" colSpan={1 + cols}>Expenses</td></tr>
						{expenses.map(s => renderISSection(s))}
						<tr className="afs-report__total-row">
							<td />
							<td className="afs-report__td-amt-bold">{fmtAmt(totalExpCur)}</td>
							{showComparatives && <td className="afs-report__td-amt-bold">{fmtAmt(totalExpPrior)}</td>}
						</tr>

						{/* Income from operations */}
						<tr>
							<td className="afs-report__td-desc afs-report__td-desc--head-pt">Income from Operations</td>
							<td className="afs-report__td-amt-bold afs-report__td-amt-bold--pt">{fmtAmt(opIncomeCur)}</td>
							{showComparatives && <td className="afs-report__td-amt-bold afs-report__td-amt-bold--pt">{fmtAmt(opIncomePrior)}</td>}
						</tr>

						{/* Other income/expenses */}
						{other.length > 0 && (
							<>
								<tr><td className="afs-report__spacer-md" colSpan={1 + cols} /></tr>
								<tr><td className="afs-report__section-head" colSpan={1 + cols}>Other Income (Expenses)</td></tr>
								{other.map(s => renderISSection(s))}
								<tr className="afs-report__total-row">
									<td />
									<td className="afs-report__td-amt-bold">{fmtAmt(totalOtherCur)}</td>
									{showComparatives && <td className="afs-report__td-amt-bold">{fmtAmt(totalOtherPrior)}</td>}
								</tr>
							</>
						)}

						{/* Income before taxes */}
						<tr>
							<td className="afs-report__td-desc afs-report__td-desc--head-pt">Income Before Income Taxes</td>
							<td className="afs-report__td-amt-bold afs-report__td-amt-bold--pt">{fmtAmt(beforeTaxCur)}</td>
							{showComparatives && <td className="afs-report__td-amt-bold afs-report__td-amt-bold--pt">{fmtAmt(beforeTaxPrior)}</td>}
						</tr>

						{/* Tax */}
						{tax.length > 0 && (
							<>
								<tr><td className="afs-report__spacer-sm" colSpan={1 + cols} /></tr>
								{tax.map(s => (
									<tr key={s.name}>
										<td className="afs-report__td-desc afs-report__td-desc--upper">{s.name}</td>
										<td className="afs-report__td-amt">{fmtAmt(s.totalCurrent)}</td>
										{showComparatives && <td className="afs-report__td-amt">{fmtAmt(s.totalPrior)}</td>}
									</tr>
								))}
							</>
						)}

						{/* Net Income */}
						<tr className="afs-report__grand-total-row">
							<td className="afs-report__td-desc afs-report__td-desc--head">Net Income</td>
							<td className="afs-report__td-amt-bold">{fmtCurrency(incomeStatement.netIncome.current)}</td>
							{showComparatives && <td className="afs-report__td-amt-bold">{fmtCurrency(incomeStatement.netIncome.prior)}</td>}
						</tr>
					</tbody>
				</table>
				<p className="afs-report__page-num">4</p>
			</div>
		);
	};

	// ─── Page 7: Notes ───────────────────────────────────────────────────────
	const NotesPage = () => {
		if (selectedNotes.length === 0 && !ppeNote) return null;

		let noteNum = 0;
		return (
			<div className="afs-report__page">
				<PageHeader title="Notes to Financial Information" subtitle={`Year Ended ${yearEndLabel}`} />
				<div className="afs-report__notes">
					{/* Note: Basis of Accounting */}
					{selectedNotes.includes('n_basis') && (
						<div className="afs-report__note">
							<p className="afs-report__h3">{++noteNum}. Basis of Accounting</p>
							<p className="afs-report__text-sm afs-report__text-sm--prewrap">{(noteTexts && noteTexts.n_basis) || NOTE_TEMPLATES.n_basis.template(compilationData)}</p>
						</div>
					)}

					{/* Note: Business description (from compilationData.businessActivity) */}
					{compilationData.businessActivity && (
						<div className="afs-report__note">
							<p className="afs-report__h3">{++noteNum}. Description of Business</p>
							<p className="afs-report__text-sm">{compilationData.businessActivity}</p>
						</div>
					)}

					{/* Note: PP&E continuity */}
					{ppeNote && ppeNote.classes && ppeNote.classes.length > 0 && (
						<div className="afs-report__note">
							<p className="afs-report__h3">{++noteNum}. Property, Plant and Equipment</p>
							<table className="afs-report__table afs-report__table--mt-sm">
								<thead>
									<tr>
										<th />
										<th colSpan={3} className="afs-report__ppe-th-year">{currentYear}</th>
										{showComparatives && <th className="afs-report__th-right">{priorYear}</th>}
									</tr>
									<tr className="afs-report__ppe-subhead-row">
										<th />
										<th className="afs-report__th-right afs-report__th-right--xs">Cost</th>
										<th className="afs-report__th-right afs-report__th-right--xs">Accum. Amort.</th>
										<th className="afs-report__th-right afs-report__th-right--xs">Net Book Value</th>
										{showComparatives && <th className="afs-report__th-right afs-report__th-right--xs">Net Book Value</th>}
									</tr>
								</thead>
								<tbody>
									{ppeNote.classes.map((cls, i) => (
										<tr key={i}>
											<td className="afs-report__td-desc afs-report__td-desc--sm">{cls.className}</td>
											<td className="afs-report__td-amt afs-report__td-amt--sm">{fmtCurrency(cls.cost.current)}</td>
											<td className="afs-report__td-amt afs-report__td-amt--sm">{fmtCurrency(cls.accumAmort.current)}</td>
											<td className="afs-report__td-amt afs-report__td-amt--sm">{fmtCurrency(cls.nbv.current)}</td>
											{showComparatives && <td className="afs-report__td-amt afs-report__td-amt--sm">{fmtCurrency(cls.nbv.prior)}</td>}
										</tr>
									))}
									<tr className="afs-report__grand-total-row">
										<td className="afs-report__td-desc afs-report__td-desc--bold" />
										<td className="afs-report__td-amt-bold afs-report__td-amt-bold--sm">{fmtCurrency(ppeNote.totals.cost.current)}</td>
										<td className="afs-report__td-amt-bold afs-report__td-amt-bold--sm">{fmtCurrency(ppeNote.totals.accumAmort.current)}</td>
										<td className="afs-report__td-amt-bold afs-report__td-amt-bold--sm">{fmtCurrency(ppeNote.totals.nbv.current)}</td>
										{showComparatives && <td className="afs-report__td-amt-bold afs-report__td-amt-bold--sm">{fmtCurrency(ppeNote.totals.nbv.prior)}</td>}
									</tr>
								</tbody>
							</table>
						</div>
					)}

					{/* Other selected notes */}
					{selectedNotes.filter(id => id !== 'n_basis' && id !== 'n_policies').filter(id => !isCustomNote(id) || hasNoteBody(id)).map(id => {
						const noteTitle = isCustomNote(id) ? (customNoteTitleById[id] || "Untitled note") : NOTE_TEMPLATES[id].title;
						const noteBody = isCustomNote(id) ? (noteTexts && noteTexts[id]) || "" : (noteTexts && noteTexts[id]) || NOTE_TEMPLATES[id].template(compilationData);
						return (
							<div key={id} className="afs-report__note">
								<p className="afs-report__h3">{++noteNum}. {noteTitle}</p>
								<p className="afs-report__text-sm afs-report__text-sm--prewrap">{noteBody}</p>
							</div>
						);
					})}
				</div>
				<p className="afs-report__page-num">5</p>
			</div>
		);
	};

	return (
		<div className="afs-report">
			<TitlePage />
			<IndexPage />
			<CompilationPage />
			<BalanceSheetPage />
			<RetainedEarningsPage />
			<IncomeStatementPage />
			<NotesPage />
		</div>
	);
	}
}

export default FullPreviewView;
